import { getTemporalIds } from '#library/temporal.library.js';
import { isString, isObject, isDefined, isUndefined, isFunction } from '#library/assertion.library.js';
import { hasKey } from '#library/primitive.library.js';
import { singular } from '#library/string.library.js';
import { getAccessors } from '#library/reflection.library.js';
import { ifDefined } from '#library/object.library.js';
import { getRelativeTime, formatNumber, formatDuration, formatList } from '#library/international.library.js';
import { defineInterpreterModule, interpret } from '../plugin/plugin.util.js';
import { findTermPlugin } from '../plugin/term/term.util.js';
import { enums, isTempo, TempoError, sym, TermHook } from '#tempo/support';
/**
 * Maps shorthand units (via enums.ELEMENT) to standard plural unit names.
 */
function resolveElementUnit(unit) {
    return (enums.ELEMENT.has(unit))
        ? `${enums.ELEMENT[unit]}s`
        : undefined;
}
/**
 * Convert a Temporal.Duration to a full Tempo.Duration object (EDO).
 */
function toDuration(dur, ctx = {}) {
    const edo = getAccessors(dur)
        .reduce((acc, d) => Object.assign(acc, ifDefined({ [d]: dur[d] })), {
        iso: dur.toString(),
        sign: dur.sign,
        blank: dur.blank,
        unit: undefined
    });
    Object.defineProperty(edo, 'balance', {
        value: function (opts = {}) {
            const { nominal, largestUnit = 'year', relativeTo: customAnchor } = opts;
            if (nominal) {
                // Mathematical mapping logic
                let { years, months, weeks, days, hours, minutes, seconds, milliseconds, microseconds, nanoseconds } = dur;
                let totalDays = days + weeks * 7 + months * 30 + years * 365;
                years = Math.trunc(totalDays / 365);
                totalDays -= years * 365;
                months = Math.trunc(totalDays / 30);
                totalDays -= months * 30;
                weeks = Math.trunc(totalDays / 7);
                totalDays -= weeks * 7;
                days = totalDays;
                const newDur = Temporal.Duration.from({
                    years, months, weeks, days, hours, minutes, seconds, milliseconds, microseconds, nanoseconds,
                    sign: dur.sign
                });
                return toDuration(newDur, { ...ctx, relativeTo: customAnchor || ctx.relativeTo });
            }
            // Strict Temporal balancing
            const anchor = customAnchor || ctx.relativeTo;
            if (!anchor)
                throw new TempoError("A relativeTo anchor is required for strict balancing. Pass an anchor or use { nominal: true } for mathematical balancing.");
            const temporalUnit = resolveElementUnit(largestUnit) ?? largestUnit;
            const balanced = dur.round({ largestUnit: temporalUnit, relativeTo: anchor });
            return toDuration(balanced, { ...ctx, relativeTo: anchor });
        },
        enumerable: false
    });
    Object.defineProperty(edo, 'format', {
        value: function (opts = {}) {
            const { locales, ...intlOpts } = opts;
            const locale = locales || ctx.locale;
            if (isFunction(ctx.durationFormat))
                return ctx.durationFormat(this);
            // 1. Native Intl.DurationFormat
            if (hasKey(Intl, 'DurationFormat', 'function'))
                return formatDuration(this, locale, { ...(ctx.durationFormat || {}), ...intlOpts });
            // 2. Fallback Polyfill (combine all non-zero units)
            const units = ['years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds', 'milliseconds', 'microseconds', 'nanoseconds'];
            const parts = [];
            for (const u of units) {
                const val = this[u];
                if (val) {
                    const unitName = singular(u); // singularize unit name (e.g., 'years' -> 'year')
                    parts.push(formatNumber(val, locale, {
                        style: 'unit',
                        unit: unitName,
                        unitDisplay: 'long',
                        ...(ctx.numberFormat || {}),
                    }));
                }
            }
            if (parts.length === 0)
                return '0'; // fallback for completely empty duration
            return formatList(parts, locale, 'conjunction', 'long');
        },
        enumerable: false
    });
    return edo;
}
/**
 * Internal implementation of Tempo.until and Tempo.since
 */
function duration(type, arg, until) {
    const since = type === 'since';
    let value, opts = {}, unit;
    switch (true) {
        case isString(arg) && (enums.ELEMENT.values().includes(singular(arg)) || enums.ELEMENT.has(arg)):
            unit = arg;
            ({ value, ...opts } = until || {});
            break;
        case isString(arg):
            value = arg;
            if (isObject(until))
                ({ unit, ...opts } = until);
            else
                unit = until;
            break;
        case isObject(arg) && isTempo(arg):
            value = arg.toDateTime();
            if (isObject(until))
                ({ unit, ...opts } = until);
            else
                unit = until;
            break;
        case isObject(arg) && isObject(until):
            ({ value, unit, ...opts } = Object.assign({ value: arg }, until));
            break;
        case isString(until):
            unit = until;
            value = arg;
            break;
        case isObject(until):
            ({ unit, ...opts } = until);
            value = arg;
            break;
        case isObject(arg) && isDefined(arg.unit):
            ({ unit, value, ...opts } = arg);
            break;
        default:
            value = arg;
    }
    const selfZdt = this.toDateTime();
    const offset = new this.constructor(value, { ...opts, anchor: this, mode: enums.MODE.Strict });
    const offsetZdt = offset.toDateTime();
    const [selfTz, selfCal] = getTemporalIds(selfZdt);
    const [offsetTz] = getTemporalIds(offsetZdt);
    // Term Lifecycle Hook: [TermHook.diff]
    if (isString(unit) && unit.startsWith('#')) {
        const [termPart] = unit.slice(1).split('.');
        const termObj = findTermPlugin(termPart, this[sym.$Internal]?.());
        if (termObj && isFunction(termObj[TermHook.diff])) {
            const targetTempo = isTempo(offset) ? offset : new this.constructor(offsetZdt, this.config);
            const diffRes = termObj[TermHook.diff](targetTempo, unit, this);
            if (isDefined(diffRes))
                return since ? `${diffRes} ${unit}` : diffRes;
        }
    }
    let temporalUnit = unit;
    if (isDefined(temporalUnit)) {
        temporalUnit = resolveElementUnit(temporalUnit) ?? `${singular(temporalUnit)}s`;
    }
    const diffZone = selfTz !== offsetTz;
    const dur = selfZdt.until(offsetZdt.withCalendar(selfCal), { largestUnit: diffZone ? 'hours' : (temporalUnit ?? 'years') });
    if (isDefined(unit))
        unit = temporalUnit;
    if (isUndefined(unit) || since) {
        const locale = this?.config?.locale;
        const numberFormat = opts['intl']?.numberFormat || this?.config?.intl?.numberFormat;
        const durationFormat = opts['intl']?.durationFormat || this?.config?.intl?.durationFormat;
        const res = toDuration(dur, { relativeTo: selfZdt, locale, numberFormat, durationFormat });
        if (unit)
            res.unit = unit;
        if (!since)
            return res;
        // --- since logic ---
        const date = [dur.years, dur.months, dur.days];
        const time = [dur.hours, dur.minutes, dur.seconds];
        const fraction = [dur.milliseconds, dur.microseconds, dur.nanoseconds]
            .map(Math.abs)
            .map(nbr => nbr.toString().padStart(3, '0'))
            .join('');
        const rtConfig = this.config.intl?.relativeTimeFormat;
        const rtOptions = opts['intl']?.relativeTimeFormat;
        const rtf = (isFunction(rtOptions) ? rtOptions : rtOptions?.format)
            || (isFunction(rtConfig) ? rtConfig : rtConfig?.format)
            || opts['rtfFormat'] || this.config['rtfFormat'];
        const getOpt = (key, legacy, def) => rtOptions?.[key] || rtConfig?.[key] || opts[legacy] || this.config[legacy] || def;
        const getFormatted = (val, u) => {
            const su = singular(u);
            if (isFunction(rtf))
                return rtf(val, su);
            if (rtf instanceof Intl.RelativeTimeFormat)
                return rtf.format(val, su);
            const style = getOpt('style', 'rtfStyle', 'narrow');
            const numeric = getOpt('numeric', 'rtfNumeric', 'always');
            return getRelativeTime(val, su, locale, style, numeric);
        };
        switch (res.unit) {
            case 'years': return getFormatted(date[0], res.unit);
            case 'months': return getFormatted(date[1], res.unit);
            case 'weeks': return getFormatted(res.weeks, res.unit);
            case 'days': return getFormatted(date[2], res.unit);
            case 'hours': return getFormatted(time[0], res.unit);
            case 'minutes': return getFormatted(time[1], res.unit);
            case 'seconds': return getFormatted(time[2], res.unit);
            case 'milliseconds':
            case 'microseconds':
            case 'nanoseconds':
                return `${fraction}`;
            default:
                return dur.toString();
        }
    }
    return dur.total({ relativeTo: selfZdt, unit });
}
/**
 * Bi-directional conversion utility for ISO Durations.
 * string -> EDO
 * DurationLikeObject -> EDO (with iso string)
 */
duration.toDuration = (input, ctx) => {
    let mappedInput = input;
    if (isObject(input)) {
        mappedInput = { ...input };
        for (const [k, v] of Object.entries(input)) {
            const resolved = resolveElementUnit(k);
            if (resolved) {
                mappedInput[resolved] = v;
                delete mappedInput[k];
            }
        }
    }
    const dur = Temporal.Duration.from(mappedInput);
    return toDuration(dur, ctx);
};
/**
 * Functional Module to attach duration methods to Tempo.
 */
export const DurationModule = defineInterpreterModule('DurationModule', duration, {
    duration(input) {
        const ctx = {
            locale: this.config?.locale,
            numberFormat: this.config?.intl?.numberFormat,
            durationFormat: this.config?.intl?.durationFormat,
        };
        return interpret(this, 'DurationModule', 'toDuration', false, input, ctx);
    }
});
